import {
	Component,
	output,
	ChangeDetectionStrategy,
	inject,
} from '@angular/core';
import { Vflow, CustomNodeComponent } from 'ngx-vflow';
import {
	DynamicModalWrapperService,
	GameData,
	PluginType,
} from '@meta-press/shared-plugins';
import { EditorGameDefinitionService } from '../../services/editor-game-definition.service';

// --- Description of game data component node
export interface GameDataNodeData {
	gameData: GameData;
	path: number[];
	displayText: string;
}

@Component({
	templateUrl: './game-data-node.component.html',
	styleUrl: './game-data-node.component.scss',
	standalone: true,
	imports: [Vflow],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GameDataNodeComponent extends CustomNodeComponent<GameDataNodeData> {
	private modalService = inject(DynamicModalWrapperService);
	private editorGameDefinitionService = inject(EditorGameDefinitionService);

	readonly nodeClicked = output<{ gameData: GameData; path: number[] }>();
	readonly gearClicked = output<{ gameData: GameData; path: number[] }>();

	onClick() {
		const nodeData = this.data();
		if (nodeData) {
			this.nodeClicked.emit({
				gameData: nodeData.gameData,
				path: nodeData.path,
			});
		}
	}

	getNodeTypeName(): string {
		const nodeData = this.data();
		if (!nodeData?.gameData) return 'GameData';

		return (
			nodeData.gameData.constructor.name.replace('GameData', '') ||
			'GameData'
		);
	}

	async onGearClick(event: Event): Promise<void> {
		// Prevent the event from bubbling up to the node click
		event.stopPropagation();

		const nodeData = this.data();
		if (!nodeData?.gameData) return;

		// Get the plugin type from the gameData
		const pluginType = this.getPluginTypeFromGameData(nodeData.gameData);
		if (pluginType === PluginType.None) {
			console.warn('No plugin type found for this game data');
			return;
		}

		// Find the editor for this plugin type
		const pluginEditor =
			this.editorGameDefinitionService.editorGameDefinitions.find(
				(plugin) => plugin.type === pluginType,
			);

		if (!pluginEditor?.editorComponent) {
			console.warn(
				'No editor component found for plugin type:',
				pluginType,
			);
			return;
		}

		const editorComponent = await pluginEditor.editorComponent();

		if (!editorComponent) {
			console.warn(
				'Editor component could not be loaded for plugin type:',
				pluginType,
			);
			return;
		}
		// Open the editor modal
		const modalRef = this.modalService.openComponentModal(editorComponent, {
			size: 'lg',
			scrollable: true,
			windowClass: 'modal-xxl',
		});

		// Set the component as slave mode and pass the current game data
		modalRef.componentInstance.IsSlave = true;
		modalRef.componentInstance.gameData = nodeData.gameData;

		// Handle the result when modal closes
		modalRef.closed.subscribe((result: GameData) => {
			if (result) {
				// Emit the gear clicked event with updated data
				this.gearClicked.emit({
					gameData: result,
					path: nodeData.path,
				});
			} else {
				console.log('Modal closed without changes');
			}
		});
	}

	private getPluginTypeFromGameData(gameData: GameData): PluginType {
		const className = gameData.constructor.name;

		// Find the plugin definition that matches the gameData type
		const pluginDefinition =
			this.editorGameDefinitionService.editorGameDefinitions.find(
				(def) => {
					if (!def.type) return false;

					// Create an instance to get the constructor name
					try {
						const instance = new def.gameDataType();
						return instance.constructor.name === className;
					} catch {
						// If instantiation fails, fallback to comparing the constructor directly
						return def.gameDataType.name === className;
					}
				},
			);

		return pluginDefinition?.type || PluginType.None;
	}
}
